import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, Layers, Loader2, Package, Search, Wrench, X, Briefcase } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  CategoryCode, CategoryGroup, CategorySection, CategoryTree,
  getCategoryTree, searchCategoryCodes,
} from '../api/lots';
import { MAX_CATEGORY_CODES, PickedCode } from '../utils/categorySelection';
import { CategoryCountSelection, categoryCountKey } from '../api/categoryCounts';
import { useVisibleCategoryCounts } from '../hooks/useVisibleCategoryCounts';
import CategoryCountBadge from './CategoryCountBadge';
import { getPreviewCategoryTree, searchPreviewCategoryCodes } from '../api/procurementPreview';
export type { PickedCode } from '../utils/categorySelection';

export interface PickedCategory {
  code: string;      // код ЕНС ТРУ ('' если выбран весь раздел)
  section?: string;  // раздел КПВЭД
  group?: CategoryGroup; // группа товары/работы/услуги (для выбора всего раздела)
  label: string;     // отображаемое имя
}

interface Props {
  preview?: boolean;
  open: boolean;
  onClose: () => void;
  onPick: (picked: PickedCategory) => void;          // «весь раздел» — одиночный выбор
  selected?: PickedCode[];                            // уже выбранные коды
  onApply?: (codes: PickedCode[]) => void;            // мультивыбор кодов
}

const GROUP_META: { key: CategoryGroup; icon: React.ReactNode }[] = [
  { key: 'goods', icon: <Package className="w-5 h-5" /> },
  { key: 'works', icon: <Wrench className="w-5 h-5" /> },
  { key: 'services', icon: <Briefcase className="w-5 h-5" /> },
];

const CODES_PAGE = 30;

export default function EnstruCategoryPicker({ open, onClose, onPick, selected, onApply, preview = false }: Props) {
  const { t, language } = useLanguage();
  const [tree, setTree] = useState<CategoryTree | null>(null);
  const [group, setGroup] = useState<CategoryGroup | null>(null);
  const [section, setSection] = useState<CategorySection | null>(null);
  const [codes, setCodes] = useState<CategoryCode[]>([]);
  const [codesTotal, setCodesTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [treeFailed, setTreeFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const [chosen, setChosen] = useState<PickedCode[]>([]);
  const dialogRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const latestSelected = useRef(selected);
  const latestClose = useRef(onClose);
  latestSelected.current = selected;
  latestClose.current = onClose;

  useEffect(() => {
    if (!open) return;
    setChosen(latestSelected.current || []);
    setGroup(null);
    setSection(null);
    setCodes([]);
    setSearchInput('');
    setSearch('');
    setPage(0);
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        latestClose.current();
      }
      if (event.key !== 'Tab') return;
      const controls = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [tabindex="0"]');
      if (!controls?.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setTreeFailed(false);
    (preview ? getPreviewCategoryTree : getCategoryTree)(controller.signal).then((data) => {
      if (!controller.signal.aborted) setTree(data);
    }).catch(() => {
      if (!controller.signal.aborted) setTreeFailed(true);
    });
    return () => controller.abort();
  }, [open, retry, preview]);

  // дебаунс поиска по кодам
  useEffect(() => {
    const id = setTimeout(() => { setSearch(searchInput); setPage(0); }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    if (!open || (!section && !search.trim())) return;
    const controller = new AbortController();
    setLoading(true);
    setLoadFailed(false);
    (preview ? searchPreviewCategoryCodes : searchCategoryCodes)({
        group: group || undefined,
        section: section?.section,
        search: search.trim() || undefined,
        skip: page * CODES_PAGE,
        limit: CODES_PAGE,
      }, controller.signal).then((data) => {
      if (controller.signal.aborted) return;
      setCodes(data.items);
      setCodesTotal(data.total);
    }).catch(() => {
      if (controller.signal.aborted) return;
      setCodes([]);
      setCodesTotal(0);
      setLoadFailed(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [open, group, section, search, page, retry, preview]);

  const showsCodes = !!section || !!search.trim();
  const countSelections = useMemo<CategoryCountSelection[]>(() => {
    if (showsCodes) return codes.map((code) => ({ code: code.code }));
    return group ? (tree?.[group] || []).map((row) => ({ section: row.section, group })) : [];
  }, [showsCodes, codes, group, tree]);
  const categoryCounts = useVisibleCategoryCounts(countSelections, !preview && open && (!showsCodes || !loading), scrollRef);

  if (!open) return null;

  const localized = (ru?: string | null, kk?: string | null) => (language === 'kk' ? kk || ru : ru || kk) || '';
  const codeName = (c: CategoryCode) => localized(c.nameRu, c.nameKz) || c.code;
  const sectionName = (s: CategorySection) => localized(s.name_ru, s.name_kz) || s.section;
  const totalPages = Math.ceil(Math.min(codesTotal, preview ? 5010 : Infinity) / CODES_PAGE);
  const isChosen = (code: string) => chosen.some((c) => c.code === code);
  const toggleCode = (c: CategoryCode) => {
    if (!onApply) {
      onPick({ code: c.code, label: codeName(c) });
      return;
    }
    setChosen((prev) => (prev.some((x) => x.code === c.code)
      ? prev.filter((x) => x.code !== c.code)
      : prev.length < MAX_CATEGORY_CODES ? [...prev, { code: c.code, label: codeName(c) }] : prev));
  };

  const reset = () => { setGroup(null); setSection(null); setCodes([]); setSearchInput(''); setSearch(''); setPage(0); };
  const close = () => { reset(); onClose(); };
  const apply = () => { if (onApply) { onApply(chosen); } reset(); };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={close}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col max-h-[90vh] sm:max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          {(group || section) && (
            <button
              type="button"
              aria-label={t('lots.picker.back')}
              onClick={() => { setPage(0); section ? setSection(null) : setGroup(null); }}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          <Layers className="w-5 h-5 text-goszakup-600" />
          <h3 id={titleId} className="font-extrabold text-gray-900 flex-1 truncate">
            {section
              ? sectionName(section)
              : group
                ? t(`lots.picker.${group}`)
                : t('lots.picker.title')}
          </h3>
          <button type="button" aria-label={t('lots.picker.close')} onClick={close} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Глобальный поиск по наименованию категории */}
        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              aria-label={t('lots.picker.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('lots.picker.searchPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-goszakup-500/40"
            />
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4">
          {treeFailed && !section && !search.trim() && (
            <p role="alert" className="mb-3 text-sm text-red-600">
              {t('lots.picker.loadFailed')}{' '}
              <button type="button" onClick={() => setRetry((value) => value + 1)} className="font-semibold underline">{t('lots.retry')}</button>
            </p>
          )}
          {/* Уровень 1: группы */}
          {!group && !search.trim() && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {GROUP_META.map(({ key, icon }) => (
                <button
                  key={key}
                  onClick={() => { setGroup(key); setSection(null); }}
                  className="flex flex-col items-start gap-2 p-4 rounded-2xl border border-gray-200 bg-white hover:border-goszakup-300 hover:shadow-sm transition-all text-left"
                >
                  <span className="w-10 h-10 rounded-xl bg-goszakup-50 text-goszakup-600 flex items-center justify-center">{icon}</span>
                  <span className="font-bold text-gray-900">{t(`lots.picker.${key}`)}</span>
                  <span className="text-xs text-gray-400">
                    {tree ? `${tree[key].reduce((n, s) => n + s.codes, 0).toLocaleString('ru-RU')} ${t('lots.picker.codesCount')}` : '…'}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Уровень 2: разделы КПВЭД */}
          {group && !section && !search.trim() && (
            <div className="space-y-1.5">
              {(tree?.[group] || []).map((s) => (
                <button
                  key={s.section}
                  onClick={() => { setSection(s); setPage(0); }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-goszakup-200 hover:bg-goszakup-50/40 transition-colors text-left"
                >
                  <span className="text-[11px] font-bold text-goszakup-700 bg-goszakup-50 px-2 py-1 rounded-lg shrink-0">{s.section}</span>
                  <span className="flex-1 text-sm font-medium text-gray-800 line-clamp-2">{sectionName(s)}</span>
                  {!preview && <CategoryCountBadge countKey={categoryCountKey({ section: s.section, group })} count={categoryCounts[categoryCountKey({ section: s.section, group })]} />}
                </button>
              ))}
            </div>
          )}

          {/* Уровень 3: коды (или глобальный поиск) */}
          {(section || search.trim()) && (
            <>
              {section && (
                <button
                  onClick={() => { onPick({ code: '', section: section.section, group: group || undefined, label: `${section.section} — ${sectionName(section)}` }); reset(); }}
                  className="w-full mb-3 px-4 py-3 rounded-xl bg-goszakup-600 text-white text-sm font-semibold hover:bg-goszakup-700 transition-colors"
                >
                  {t('lots.picker.wholeSection')}
                </button>
              )}
              {loading ? (
                <div className="flex justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : loadFailed ? (
                <p role="alert" className="text-center text-sm text-red-600 py-8">
                  {t('lots.picker.loadFailed')}{' '}
                  <button type="button" onClick={() => setRetry((value) => value + 1)} className="font-semibold underline">{t('lots.retry')}</button>
                </p>
              ) : codes.length === 0 ? (
                <p className="text-center text-sm text-gray-400 py-12">{t('lots.picker.empty')}</p>
              ) : (
                <div className="space-y-1.5">
                  {codes.map((c) => {
                    const on = isChosen(c.code);
                    const description = localized(c.descRu, c.descKz);
                    return (
                      <button
                        type="button"
                        key={c.code}
                        role={onApply ? 'checkbox' : undefined}
                        aria-checked={onApply ? on : undefined}
                        disabled={!!onApply && !on && chosen.length >= MAX_CATEGORY_CODES}
                        onClick={() => toggleCode(c)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-colors text-left disabled:opacity-40 disabled:cursor-not-allowed ${
                          on
                            ? 'border-goszakup-400 bg-goszakup-50/70 ring-1 ring-goszakup-300'
                            : 'border-gray-100 bg-white hover:border-goszakup-200 hover:bg-goszakup-50/40'
                        }`}
                      >
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                          on ? 'bg-goszakup-600 border-goszakup-600 text-white' : 'border-gray-300 bg-white text-transparent'
                        }`}>
                          <Check className="w-3.5 h-3.5" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm text-gray-800 line-clamp-2">{codeName(c)}</span>
                          {/* код + характеристика: иначе одноимённые позиции («Бумага» А6/Б3/Б4) неразличимы */}
                          <span className="block text-[11px] text-gray-400 truncate">
                            {c.code}{description ? ` · ${description}` : ''}
                          </span>
                        </span>
                        {!preview && <CategoryCountBadge countKey={categoryCountKey({ code: c.code })} count={categoryCounts[categoryCountKey({ code: c.code })]} />}
                      </button>
                    );
                  })}
                </div>
              )}
              {!loading && !loadFailed && totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 pt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 hover:bg-gray-50"
                  >
                    ←
                  </button>
                  <span className="text-xs text-gray-500">{page + 1} / {totalPages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-600 disabled:opacity-40 hover:bg-gray-50"
                  >
                    →
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Футер мультивыбора */}
        {onApply && (
          <div className="flex flex-wrap items-center gap-3 px-5 py-3.5 border-t border-gray-100 bg-white sm:rounded-b-2xl">
            <span aria-live="polite" className="text-xs text-gray-500 flex-1 min-w-[130px]">
              {chosen.length > 0
                ? `${t('lots.picker.selectedLabel')}: ${chosen.length} / ${MAX_CATEGORY_CODES}`
                : t('lots.picker.selectHint')}
              {chosen.length >= MAX_CATEGORY_CODES && <span className="block mt-1 text-amber-700">{t('lots.picker.limit')}</span>}
            </span>
            {chosen.length > 0 && (
              <button
                onClick={() => setChosen([])}
                className="px-3.5 py-2 rounded-xl text-sm font-semibold text-gray-500 border border-gray-200 hover:bg-gray-50"
              >
                {t('lots.picker.clear')}
              </button>
            )}
            <button
              onClick={apply}
              className="px-5 py-2 rounded-xl text-sm font-bold bg-goszakup-600 text-white hover:bg-goszakup-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t('lots.picker.apply')}{chosen.length > 0 ? ` (${chosen.length})` : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
